import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  HomeIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  FolderIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import PostsList from './PostsList';
import CategoriesList from './CategoriesList';
import AdminsList from './AdminsList';
import RolesList from './RolesList';
import ApiStatus from './ApiStatus';
import OnlineStatus from './OnlineStatus';
import PostEditor from './PostEditor';
import Profile from './Profile';
import SlidersDashboard from './SlidersDashboard';
import { hasPermission } from '../utils/permissions';

const Dashboard: React.FC = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Администраторы', href: '/dashboard/admins', icon: UserGroupIcon },
    { name: 'Роли', href: '/dashboard/roles', icon: UserGroupIcon },
    { name: 'Категории', href: '/dashboard/categories', icon: FolderIcon },
    { name: 'Посты', href: '/dashboard/posts', icon: DocumentTextIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile menu button */}
      <div className="md:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 bg-white shadow-lg"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900">ParkDubki Admin</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <nav className="mt-5 flex-1 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    location.pathname.includes(item.href.split('/')[2]) 
                      ? 'bg-primary-100 text-primary-700' 
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="mr-3 flex-shrink-0 h-6 w-6" />
                  {item.name}
                </Link>
              ))}
              {/* Слайдеры */}
              {(hasPermission(admin, 'sliders', 'read') || hasPermission(admin, 'slider_categories', 'read')) && (
                <Link
                  to="/dashboard/sliders"
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    location.pathname.includes('sliders')
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <FolderIcon className="mr-3 flex-shrink-0 h-6 w-6" />
                  Слайдеры
                </Link>
              )}
            </nav>
          </div>
          <div className="flex flex-col gap-2 border-t border-gray-200 p-4 mt-auto">
            <Link
              to="/dashboard/profile"
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900`}
              onClick={() => setSidebarOpen(false)}
            >
              <Cog6ToothIcon className="mr-3 flex-shrink-0 h-6 w-6" />
              Настройки профиля
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center px-2 py-2 text-sm font-medium rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            >
              <ArrowRightOnRectangleIcon className="mr-3 flex-shrink-0 h-6 w-6" />
              Выйти
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">
          <div className="py-6 md:py-6 pt-16 md:pt-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Routes>
                <Route path="/posts" element={<PostsList />} />
                <Route path="/posts/edit/:id" element={<PostEditor />} />
                <Route path="/categories" element={<CategoriesList />} />
                <Route path="/admins" element={<AdminsList />} />
                <Route path="/roles" element={<RolesList />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/sliders" element={<SlidersDashboard />} />
                <Route path="/" element={<Navigate to="/admins" />} />
              </Routes>
            </div>
          </div>
        </main>
        <ApiStatus />
        <OnlineStatus />
      </div>
    </div>
  );
};

export default Dashboard; 